import React, { useEffect, useMemo, useState } from 'react';

// Простая эвристика: если расширение/браузер форсят тёмную тему,
// то даже у явного белого div computed backgroundColor станет не белым.
function isProbablyForcedDarkMode() {
  try {
    // 1) Dark Reader обычно добавляет атрибуты/классы на html
    const html = document.documentElement;
    const htmlAttr = `${html.getAttribute('data-darkreader-mode') || ''}${
      html.getAttribute('data-darkreader-scheme') || ''
    }`;
    if (htmlAttr) return true;

    // 2) forced-colors (Windows High Contrast)
    if (
      window.matchMedia &&
      window.matchMedia('(forced-colors: active)').matches
    )
      return true;

    // 3) Проверка computed style на тестовом элементе
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;left:-9999px;top:-9999px;width:1px;height:1px;background:#ffffff;color:#000000;';
    // важное: многие форс-дарк движки меняют цвета даже на инлайне
    document.body.appendChild(probe);
    const bg = window.getComputedStyle(probe).backgroundColor;
    document.body.removeChild(probe);

    // нормализуем: white обычно rgb(255, 255, 255)
    if (bg && bg !== 'rgb(255, 255, 255)' && bg !== 'rgba(255, 255, 255, 1)') {
      return true;
    }

    return false;
  } catch {
    return false;
  }
}

export default function ForcedDarkModeBanner() {
  const [show, setShow] = useState(false);

  const details = useMemo(() => {
    const forcedColors = window.matchMedia?.(
      '(forced-colors: active)'
    )?.matches;
    const prefersDark = window.matchMedia?.(
      '(prefers-color-scheme: dark)'
    )?.matches;
    const html = document.documentElement;
    const darkReader = !!(
      html.getAttribute('data-darkreader-mode') ||
      html.getAttribute('data-darkreader-scheme')
    );
    return { forcedColors, prefersDark, darkReader };
  }, []);

  useEffect(() => {
    // небольшая задержка, чтобы успели примениться стили расширения
    const t = setTimeout(() => {
      setShow(isProbablyForcedDarkMode());
    }, 400);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  return (
    <div
      data-darkreader-ignore
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 12,
        zIndex: 999999,
        background: '#fffbe6',
        border: '1px solid #ffe58f',
        color: '#000',
        padding: '10px 12px',
        borderRadius: 10,
        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        fontSize: 14,
        lineHeight: 1.35,
      }}
      role="status"
      aria-live="polite"
    >
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>
            Похоже, у вас включён форс‑тёмный режим
          </div>
          <div style={{ opacity: 0.9 }}>
            Из-за этого фон/текст в конфигураторе отображаются «тёмное на
            тёмном». Отключите расширение типа <b>Dark Reader</b> или браузерный{' '}
            <b>Force Dark Mode</b> (Chrome flags).
          </div>
          <div style={{ marginTop: 6, opacity: 0.75, fontSize: 12 }}>
            Диагностика: darkReader={String(details.darkReader)} ·
            forced-colors={String(details.forcedColors)} · prefers-dark=
            {String(details.prefersDark)}
          </div>
        </div>
        <button
          onClick={() => setShow(false)}
          style={{
            flex: '0 0 auto',
            background: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: 8,
            padding: '6px 10px',
            cursor: 'pointer',
            color: '#000',
          }}
        >
          Закрыть
        </button>
      </div>
    </div>
  );
}
